'use client';
import { memo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { Trace, TraceCollapsible, TraceCollapsibleContent, TraceCollapsibleTrigger, TraceDescription, TraceIcon, TraceLine, TraceTitle } from '../../components/ui/shadcn/trace';
import { StatusGlyph } from '../../components/ui/StatusMark';
import { useTranslation } from '../../lib/i18n';
import { useNow } from '../../lib/useNow';
import type { BrainCard } from '../../lib/types';
import { MorePill } from '../../components/ui/MorePill';
import { useBrainChat } from './BrainChatProvider';
import { formatDuration } from '../../lib/format';
import { TODO_PREVIEW_ITEMS, todoPreviewItems, type TodoPreviewItem } from '../../lib/chatPresentation';
import { PluginChatCardHost } from '../../lib/pluginChatUi';

/** The read-only card's fold, preview window and elapsed clock. The shared heartbeat runs only
 * while the turn is live and a timestamped row runs; settled cards subscribe to no clock. */
function useCardShell<T extends TodoPreviewItem & { startedAt?: number }>(items: readonly T[], live: boolean) {
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ticking = live && items.some((item) => item.status === 'in_progress' && Number.isFinite(item.startedAt));
  const now = useNow(1000, ticking);
  const previewable = items.length > TODO_PREVIEW_ITEMS;
  const shown = collapsed ? [] : previewable && !expanded ? todoPreviewItems(items, TODO_PREVIEW_ITEMS) : [...items];
  return { collapsed, setCollapsed, expanded, setExpanded, now, previewable, shown };
}

/** The read-only card's title, completion count and fold trigger. */
function CardHead({ title, done, total }: { title: string; done: number; total: number }) {
  return <TraceCollapsibleTrigger>
    <TraceLine>
      <TraceIcon><ClipboardList /></TraceIcon>
      <TraceTitle>{title}</TraceTitle>
      {total > 0 ? <TraceDescription>{done}/{total}</TraceDescription> : null}
    </TraceLine>
  </TraceCollapsibleTrigger>;
}

/** A card nobody can act on: another plugin's checklist, a todo card emitted before task ids existed, or an
 *  addressable todo card while the plugin that serves its rows is not running. It is the rendering the
 *  transcript has always had — a glyph, the glued `text` the emitter composed, and a struck-through line
 *  once a row is done.
 *
 *  Read-only is a CORRECTNESS rule, not a missing feature. Card items are a generic mechanism, so a plugin
 *  may emit rows with ids of its own; those ids are its handles, and a tick box built from one would PATCH
 *  the todo API with something that means nothing there. */
function StaticCard({ card, live }: { card: BrainCard; live: boolean }) {
  const { t } = useTranslation();
  const items = card.items ?? [];
  const { collapsed, setCollapsed, expanded, setExpanded, now, previewable, shown } = useCardShell(items, live);
  const done = items.filter((i) => i.status === 'completed').length;
  if (items.length > 0 && items.every((item) => item.status === 'completed')) return null;
  return (
    <Trace data-testid="chat-card">
      <TraceCollapsible open={!collapsed} onOpenChange={open => setCollapsed(!open)}>
      {(card.title || items.length > 0) ? (
        <CardHead
          title={card.title ?? t.brainChat.cardFallback}
          done={done}
          total={items.length}
        />
      ) : null}
      <TraceCollapsibleContent>
      {shown.length > 0 ? (
        <Trace>
          {shown.map((titem, i) => (
            <TraceLine key={i} status={titem.status === 'in_progress' ? 'running' : titem.status === 'pending' ? 'pending' : undefined}>
              <TraceIcon><StatusGlyph kind={titem.status === 'completed' ? 'success' : titem.status === 'in_progress' ? 'working' : 'waiting'} tone={titem.status === 'completed' ? 'success' : 'muted'} /></TraceIcon>
              <TraceTitle className="whitespace-normal">
                {/* Only the task text is struck through; its duration stays readable beside it. */}
                <span className={titem.status === 'completed' ? 'line-through' : undefined}>{titem.text}</span>
                {titem.status === 'in_progress' && Number.isFinite(titem.startedAt) ? (
                  <span data-testid="chat-card-elapsed" className="ml-1 tabular-nums text-muted-foreground opacity-70">· {formatDuration(now - titem.startedAt!)}</span>
                ) : titem.status === 'completed' && Number.isFinite(titem.durationMs) ? (
                  <span data-testid="chat-card-elapsed" className="ml-1 tabular-nums text-muted-foreground opacity-70">· {formatDuration(titem.durationMs!)}</span>
                ) : null}
              </TraceTitle>
            </TraceLine>
          ))}
        </Trace>
      ) : null}
      {!collapsed && previewable ? (
        <div className="mt-1">
          <MorePill expanded={expanded} hidden={items.length - TODO_PREVIEW_ITEMS} onToggle={() => setExpanded((v) => !v)} />
        </div>
      ) : null}
      {!collapsed && card.body ? <div className="whitespace-pre-wrap text-muted-foreground">{card.body}</div> : null}
      </TraceCollapsibleContent>
      </TraceCollapsible>
    </Trace>
  );
}

/** Generic read-only fallback for loading, absent or refused plugin renderers. */
export const CardBlock = memo(function CardBlock({ card, live }: { card: BrainCard; live: boolean }) {
  return <StaticCard card={card} live={live} />;
});

/** Registered plugin renderers own interactive cards. Loading, absent and refused renderers use
 * the generic read-only fallback; renderer crashes remain owned by PluginErrorBoundary. */
export const PluginCardBlock = memo(function PluginCardBlock({ card, live }: { card: BrainCard; live: boolean }) {
  const { pluginSessionId, commands, runSlash } = useBrainChat();
  return (
    <PluginChatCardHost
      card={card}
      sessionId={pluginSessionId}
      live={live}
      open={(command?: string) => {
        const target = command ? commands.find((candidate) => candidate.name === command) : undefined;
        if (target) runSlash(target);
      }}
      fallback={<CardBlock card={card} live={live} />}
    />
  );
}, (previous, next) => previous.card === next.card && previous.live === next.live);

/** Persistent transcript controls that are not part of the answer currently streaming.
 *
 * The todo card lives here, as the transcript tail, with the plugin cards. The tail
 * is the LAST thing in the transcript, a live turn included: the answer and its tool rows arrive above
 * it. It is ordinary content, not pinned to the composer, so scrolling back through the conversation
 * carries it away like any message (`chat.tasklist-tail.e2e.ts`).
 * Memoizing the group also keeps a text delta from reconciling controls whose data did not change. */
export const TranscriptAmbientExtras = memo(function TranscriptAmbientExtras({
  variant,
  cards,
  live,
  visible,
}: {
  variant: 'full' | 'compact';
  cards: readonly BrainCard[];
  live: boolean;
  visible: boolean;
}) {
  if (!visible || cards.length === 0) return null;
  return (
    <div
      data-testid="chat-ambient-extras"
      className={`chat-extras flex flex-col gap-3 ${variant === 'full' ? 'mt-4 text-small' : 'mt-3 text-small'}`}
    >
      {cards.map((card) => <PluginCardBlock key={card.id} card={card} live={live} />)}
    </div>
  );
});
